/**
 * Buyer-Pass kennel referral attribution (client-side).
 *
 * Tiny module that does three things:
 *   1. recordKennelVisit(kennelId) — called from kennel/dog detail
 *      pages. Stores the latest kennel id in localStorage with a
 *      30-day TTL and fires a best-effort POST so we have a
 *      server-side record for analytics.
 *   2. consumeReferrer() — called from the Buyer-Pass checkout flow
 *      to read the latest kennel id and clear it (so a future
 *      re-subscribe doesn't double-attribute).
 *   3. peekReferrer() — non-destructive read for previews.
 *
 * The latest visited kennel wins (last-touch attribution). The TTL
 * matches our 30-day cookie window so a buyer who circled back in
 * three weeks still credits the kennel.
 */
import api from "@/lib/api";

const KEY = "ak9_ref_kennel";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object" || !obj.kennel_id) return null;
    if (typeof obj.expires_at !== "number" || Date.now() > obj.expires_at) {
      localStorage.removeItem(KEY);
      return null;
    }
    return obj;
  } catch {
    return null;
  }
}

function write(kennelId) {
  try {
    localStorage.setItem(KEY, JSON.stringify({
      kennel_id: kennelId,
      first_seen_at: Date.now(),
      expires_at: Date.now() + TTL_MS,
    }));
  } catch {
    /* localStorage unavailable */
  }
}

export function recordKennelVisit(kennelId) {
  if (!kennelId || typeof kennelId !== "string") return;
  write(kennelId);
  // Best-effort POST so analytics can track unique kennel visits without
  // requiring a logged-in user. Failure is non-fatal — the localStorage
  // record is the source of truth for attribution at checkout time.
  api.post(`/referrals/attribute/${encodeURIComponent(kennelId)}`).catch(() => {});
}

export function peekReferrer() {
  return read()?.kennel_id || null;
}

export function consumeReferrer() {
  const v = read();
  if (!v) return null;
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  return v.kennel_id;
}
